import { useState, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import { supabase } from '../../lib/supabase';
import type { UserSubscription } from '../../types/subscription';

export function useSubscription() {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { user, isAdmin } = useAuth();

  const isSubscriptionActive = (sub: UserSubscription | null): boolean => {
    if (!sub) return false;
    return (
      sub.status === 'active' &&
      new Date(sub.current_period_end) > new Date()
    );
  };

  const isPaidSubscription = (sub: UserSubscription | null): boolean => {
    if (!sub) return false;

    // Always return true for testing
    return true;

  };

  useEffect(() => {
    let mounted = true;

    const loadSubscription = async () => {
      try {
        if (!user) {
          if (mounted) {
            setSubscription(null);
            setError(null);
            setIsLoading(false);
          }
          return;
        }

        // Give admin users full access
        if (isAdmin) {
          const adminSub = {
            id: 'admin_subscription',
            user_id: user.id,
            plan_id: 'admin_plan',
            status: 'active',
            current_period_start: new Date().toISOString(),
            current_period_end: new Date(Date.now() + 100 * 365 * 24 * 60 * 60 * 1000).toISOString(),
            cancel_at_period_end: false,
            stripe_customer_id: null,
            stripe_subscription_id: null,
            email: user.email
          };
          
          if (mounted) {
            setSubscription(adminSub);
            setError(null);
            setIsLoading(false);
          }
          return;
        }

        const { data: subs, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (error) {
          // Retry on network errors
          if (error.code === 'PGRST116' && retryCount < 3) {
            setRetryCount(prev => prev + 1);
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
            return;
          }
          throw error;
        }

        // Get most recent active subscription
        const activeSub = subs?.length ? subs[0] : null;

        if (mounted) {
          setSubscription(activeSub);
          setError(null);
          setRetryCount(0);
        }
      } catch (err) {
        console.error('Error loading subscription:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load subscription'));
          setSubscription(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadSubscription();
    return () => {
      mounted = false;
    };
  }, [user, isAdmin, retryCount]);

  return {
    subscription,
    isLoading,
    error,
    isSubscribed: isSubscriptionActive(subscription),
    isPro: isPaidSubscription(subscription)
  };
}